import { useCallback, useEffect } from 'react';

import { Link } from 'react-router';

import { useAppDispatch, useAppSelector } from 'dux/app/hooks';

import { Box, Button, Hidden, Text, TextLink, theme } from '@prose-ui';
import { styled } from '@prose-ui/legacy';
import { mediaMinWidth } from '@prose-ui/utils/media';
import { useNpsModal } from 'Contexts/NpsModalContext';

import BaseScene from 'Apps/Checkout/Scenes/BaseScene';

import { Image, type StaticImageData } from 'Components/Image';
import { BasicPostPurchaseSuccessPromo } from '../Components/BasicPostPurchaseSuccessPromo';
import { FocusedAsideSection } from '../Components/FocusedAsideSection';
import { ThreeStepPostPurchaseSuccessPromo } from '../Components/ThreeStepPostPurchaseSuccessPromo';

import { productsCategories } from 'constants/products';

import CustomerOriginForm from '../Blocks/CustomerOriginForm';

import LoadingScene from 'Scenes/LoadingScene';

import * as trackingActions from 'dux/tracking/actions';
import { getIsCustomProductsOrder } from 'dux/postPurchaseOrder/selectors';
import {
  type ExcludeByPromoType,
  excludePromo,
  PromoIdentifiers,
  type Promotion,
  usePromoIdentifiers,
} from 'dux/promotions/hooks';

import { usePostPurchaseOfferContent } from './usePostPurchaseOfferContent';

const Badge = styled(Image)`
  position: absolute;
  top: ${theme.spacing['4x']};
  right: ${theme.spacing['4x']};
  ${mediaMinWidth('md')} {
    top: ${theme.spacing['6x']};
    right: ${theme.spacing['6x']};
    width: 136px;
    height: 136px;
  }
`;

const ButtonWrapper = styled.div`
  position: relative;
  max-width: 257px;
  min-width: 200px;
  width: 100%;
`;

const PromoImage = styled(Image)`
  position: absolute;
  display: block;
  object-fit: cover;
  width: 100%;
  height: 100%;
`;

export type Content = {
  tracking: {
    pageView: string;
  };
  title: string;
  description: string | null;
  benefits: string[] | null;
  cta: {
    label: string;
    path: string;
    dataFrom: string;
    dataClick: string;
  };
  image: {
    desktop: StaticImageData;
    mobile: StaticImageData;
  };
  badge: string;
  discount?: string;
};

type OrderSuccessPromoCommunicationProps = {
  content: Content;
  onViewed: (value: string) => void;
  promo: ExcludeByPromoType<Promotion, 'feedback'> | undefined;
};

const OrderSuccessPromoCommunication = ({
  content,
  onViewed,
  promo,
}: OrderSuccessPromoCommunicationProps) => {
  useEffect(() => {
    onViewed(content.tracking.pageView);
  }, [onViewed, content.tracking.pageView]);

  return promo?.id === PromoIdentifiers.SKINCARE_MINIS ? (
    <ThreeStepPostPurchaseSuccessPromo
      cta={
        <Button asChild size="md" variant="primary">
          <Link
            data-click={content.cta.dataClick}
            data-from={content.cta.dataFrom}
            to={content.cta.path}
          >
            {content.cta.label}
          </Link>
        </Button>
      }
    />
  ) : (
    <BasicPostPurchaseSuccessPromo
      badge={<Badge alt="promotional badge" height={88} src={content.badge} width={88} />}
      benefits={content.benefits}
      cta={
        <ButtonWrapper>
          <Button asChild size="md" variant="primary">
            <Link
              data-click={content.cta.dataClick}
              data-from={content.cta.dataFrom}
              to={content.cta.path}
            >
              {content.cta.label}
            </Link>
          </Button>
        </ButtonWrapper>
      }
      description={content.description}
      image={
        <>
          <Hidden below="tablet">
            <PromoImage alt="products" src={content.image.desktop} />
          </Hidden>
          <Hidden above="mobile">
            <PromoImage alt="products" src={content.image.mobile} />
          </Hidden>
        </>
      }
      title={content.title}
    />
  );
};

const Summary = styled.div`
  padding: ${theme.spacing['8x']} ${theme.spacing['6x']} 0 ${theme.spacing['6x']};
  ${mediaMinWidth('md')} {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing['4x']};

    max-width: 492px;
    margin: 0 auto;
    padding: ${theme.spacing['14x']} 0 0;
  }
`;

const OriginBlock = styled.div`
  gap: ${theme.spacing['4x']};
  margin-top: ${theme.spacing['10x']};
  padding: ${theme.spacing['4x']} ${theme.spacing['6x']};
  background-color: ${theme.colors.neutral[300]};

  ${mediaMinWidth('md')} {
    padding: ${theme.spacing['4x']} ${theme.spacing['6x']} ${theme.spacing['4x']}
      ${theme.spacing['14x']};
  }
`;

const OrderDetailsWrapper = styled.div`
  scroll-margin-top: ${theme.spacing['20x']};
  ${mediaMinWidth('md')} {
    max-width: 492px;
    margin: 0 auto;
    padding-bottom: ${theme.spacing['10x']};
  }
`;

const Divider = styled.hr`
  width: 100%;
  margin-top: ${theme.spacing['10x']};
  margin-bottom: ${theme.spacing['10x']};
  max-width: 492px;
  border: none;
  border-top: 1px solid ${theme.colors.neutral[700]};
`;

const OrderNumber = styled.div`
  margin-top: ${theme.spacing['2x']};
`;

const ShippingData = styled.div`
  margin-bottom: ${theme.spacing['8x']};
`;

const OrderSendingInformation = styled.div`
  margin-bottom: ${theme.spacing['8x']};
  margin-top: ${theme.spacing['4x']};

  ${mediaMinWidth('md')} {
    margin-top: 0;
    margin-bottom: 0;
  }
`;

export type ProductsCategories =
  | typeof productsCategories.HAIRCARE
  | typeof productsCategories.SKINCARE;

type Props = {
  order: {
    pubkey: string;
    items: {
      pubkey: string;
    }[];
    formulaset?: {
      categories?: ProductsCategories[];
    };
    is_subscription: boolean;
    is_gift: boolean;
    payment_info: {
      brand: string;
      name?: string;
      last4: string;
      exp_month: number;
      exp_year: number;
    };
    serial_no: string;
    shipping_address?: {
      name?: string;
      address1?: string;
      address2?: string;
      city?: string;
      zipcode?: string;
      state?: string;
      country?: string;
    };
    total_amount: number;
    total_credit?: number;
    total_offer?: number;
    total_gift?: number;
    total_price?: number;
    payment_from_customer: number;
    total_taxes?: number;
    total_shipping?: number;
    total_subscription?: number;
  };
  user: {
    email: string;
  };
  customerOriginState: {
    exists: boolean;
    success: boolean;
  };
  onSubmitCustomerOrigin: () => void;
  customerOrigins: {
    isLoading: boolean;
    hasError: boolean;
    data: { name: string; pubkey: string }[];
  };
  promoOverride?: ExcludeByPromoType<Promotion, 'feedback'>;
  isShipNow?: boolean;
};

export const PostPurchaseSuccessV2 = ({
  order,
  user,
  customerOrigins,
  customerOriginState,
  onSubmitCustomerOrigin,
  promoOverride,
  isShipNow = false,
}: Props) => {
  const { openNpsModal } = useNpsModal();
  const isCustomProductsOrder = useAppSelector(getIsCustomProductsOrder);

  // In some cases (e.g. gift flow) the shipping address might not exist
  const shippingAddress = order?.shipping_address;

  const dispatch = useAppDispatch();
  const trackViewed = useCallback(
    (eventName: string) => dispatch(trackingActions.trackEvent(eventName)),
    [],
  );
  const orderCategory = order?.formulaset?.categories?.[0];
  const content = usePostPurchaseOfferContent(promoOverride, orderCategory);

  const { availablePromoList } = usePromoIdentifiers('post-purchase-success');
  const promo = promoOverride ?? excludePromo(availablePromoList, 'feedback')[0];

  const ctaContent = isCustomProductsOrder
    ? {
        label: 'Learn about your products',
        to: `/digital-leaflet/${order.pubkey}`,
        dataClick: 'learn-products',
        dataFrom: 'post-purchase-order-details',
      }
    : {
        label: 'View order history',
        to: '/account/history',
        dataClick: 'order-history',
        dataFrom: 'post-purchase-order-details',
      };

  useEffect(() => {
    if (order.pubkey) {
      openNpsModal({ orderPubkey: order.pubkey });
    }
  }, [order]);

  return order ? (
    <BaseScene header={!isShipNow}>
      <FocusedAsideSection
        slots={{
          beforeFocus: (
            <>
              <Summary>
                <div data-testid="post-purchase-success-title">
                  <Hidden below="desktop">
                    <Text variant="heading5">Thank you for your order!</Text>
                  </Hidden>
                  <Hidden above="tablet">
                    <Text variant="heading6">Thank you for your order!</Text>
                  </Hidden>
                </div>
                <OrderSendingInformation>
                  <Text>
                    Your order confirmation was sent to{' '}
                    <span data-testid="post-purchase-success-email">{user.email}</span>
                  </Text>
                  <Hidden above="tablet">
                    <TextLink asChild variant="simple">
                      <a
                        data-testid="post-purchase-success-order-anchor-link"
                        href="#order-details"
                      >
                        See order details
                      </a>
                    </TextLink>
                  </Hidden>
                </OrderSendingInformation>
              </Summary>
              <Hidden below="desktop">
                <Divider />
              </Hidden>
            </>
          ),
          focus: (
            <OrderSuccessPromoCommunication
              content={content}
              onViewed={trackViewed}
              promo={promo}
            />
          ),
          afterFocus: (
            <OrderDetailsWrapper id="order-details">
              <Box
                marginTop={{ mobile: '14x', desktop: 'none' }}
                paddingX={{ mobile: '6x', desktop: 'none' }}
                paddingY={{ mobile: '8x', desktop: 'none' }}
              >
                <Text variant="heading6">Order details:</Text>
                <OrderNumber>
                  Your order number is{' '}
                  <span data-testid="post-purchase-success-order-number">{order.serial_no}</span>
                </OrderNumber>
                <br />
                {shippingAddress && (
                  <ShippingData>
                    <div>Your order ships to:</div>
                    <div data-testid="post-purchase-success-address">
                      {shippingAddress.name && <div>{shippingAddress.name}</div>}
                      {shippingAddress.address1 && <div>{shippingAddress.address1}</div>}
                      {shippingAddress.address2 && <div>{shippingAddress.address2}</div>}
                      <div>
                        {shippingAddress.city && <span>{shippingAddress.city}</span>}
                        {shippingAddress.zipcode && <span>, {shippingAddress.zipcode}</span>}
                        {shippingAddress.state && <span> {shippingAddress.state}</span>}
                      </div>
                      {shippingAddress.country && <div>{shippingAddress.country}</div>}
                    </div>
                  </ShippingData>
                )}
                <TextLink asChild variant="primary">
                  <Link
                    data-click={ctaContent.dataClick}
                    data-from={ctaContent.dataFrom}
                    to={ctaContent.to}
                  >
                    {ctaContent.label}
                  </Link>
                </TextLink>
              </Box>
              {(!customerOriginState.exists ||
                (customerOriginState.exists && customerOriginState.success)) &&
                !customerOrigins.isLoading &&
                customerOrigins.data && (
                  <OriginBlock>
                    <CustomerOriginForm
                      customerOrigins={customerOrigins.data}
                      onSubmit={onSubmitCustomerOrigin}
                      success={customerOriginState.success}
                    />
                  </OriginBlock>
                )}
            </OrderDetailsWrapper>
          ),
        }}
      />
    </BaseScene>
  ) : (
    <LoadingScene header={false} pastDelay />
  );
};
